// ============================================================
// HARMONIC CHARTER — ChartRenderer
// ============================================================

import React, { useCallback, useEffect, useRef } from 'react';
import { useSongStore } from '../../store/songStore';
import { SectionBlock } from './SectionBlock';
import { AnnotationLayer } from '../annotations/AnnotationLayer';

interface ChartRendererProps {
  annotationMode: boolean;
  onExitAnnotationMode: () => void;
}

export function ChartRenderer({ annotationMode, onExitAnnotationMode }: ChartRendererProps) {
  const song         = useSongStore((s) => s.song);
  const selection    = useSongStore((s) => s.selection);
  const setSelection = useSongStore((s) => s.setSelection);
  const addSection   = useSongStore((s) => s.addSection);
  const addBar       = useSongStore((s) => s.addBar);
  const undo         = useSongStore((s) => s.undo);
  const redo         = useSongStore((s) => s.redo);
  const canUndo      = useSongStore((s) => s.canUndo);
  const canRedo      = useSongStore((s) => s.canRedo);

  const chartRef = useRef<HTMLDivElement>(null);

  // ── Keyboard shortcuts ────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const ctrlOrCmd = navigator.platform.includes('Mac') ? e.metaKey : e.ctrlKey;
      if (ctrlOrCmd && e.key === 'z' && !e.shiftKey) {
        e.preventDefault(); if (canUndo()) undo(); return;
      }
      if ((ctrlOrCmd && e.key === 'y') || (ctrlOrCmd && e.shiftKey && e.key === 'z')) {
        e.preventDefault(); if (canRedo()) redo(); return;
      }
      if (e.key === 'Escape' && !selection) setSelection(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, canUndo, canRedo, selection, setSelection]);

  // ── Select bar handler ────────────────────────────────────
  const handleSelectBar = useCallback(
    (sectionId: string, barId: string) => setSelection({ sectionId, barId }),
    [setSelection],
  );

  // ── Navigate bar handler ──────────────────────────────────
  const handleNavigateBar = useCallback(
    (
      sectionId: string,
      barId: string,
      direction: 'prev' | 'next' | 'up' | 'down' | 'nextSection',
    ) => {
      const sections   = useSongStore.getState().song.sections;
      const barsPerRow = useSongStore.getState().song.barsPerRow ?? 4;

      const sectionIdx = sections.findIndex((s) => s.id === sectionId);
      if (sectionIdx === -1) return;
      const section = sections[sectionIdx];
      const barIdx  = section.bars.findIndex((b) => b.id === barId);
      if (barIdx === -1) return;

      const sectionBarsPerRow = section.barsPerRow ?? barsPerRow;

      switch (direction) {
        case 'next': {
          if (barIdx < section.bars.length - 1) {
            // There's a next bar in this section — go to it
            setSelection({ sectionId, barId: section.bars[barIdx + 1].id });
          } else {
            // Last bar of this section — add a new bar and navigate to it
            addBar(sectionId);
            // Zustand updates are synchronous — read the new state immediately
            const latest = useSongStore.getState().song.sections.find((s) => s.id === sectionId);
            if (latest && latest.bars.length > 0) {
              setSelection({ sectionId, barId: latest.bars[latest.bars.length - 1].id });
            }
          }
          break;
        }
        case 'prev': {
          if (barIdx > 0) {
            setSelection({ sectionId, barId: section.bars[barIdx - 1].id });
          } else if (sectionIdx > 0) {
            const prevSection = sections[sectionIdx - 1];
            if (prevSection.bars.length > 0) {
              setSelection({
                sectionId: prevSection.id,
                barId: prevSection.bars[prevSection.bars.length - 1].id,
              });
            }
          }
          break;
        }
        case 'up': {
          const targetIdx = barIdx - sectionBarsPerRow;
          if (targetIdx >= 0) {
            setSelection({ sectionId, barId: section.bars[targetIdx].id });
          }
          break;
        }
        case 'down': {
          const targetIdx = barIdx + sectionBarsPerRow;
          if (targetIdx < section.bars.length) {
            setSelection({ sectionId, barId: section.bars[targetIdx].id });
          }
          break;
        }
        case 'nextSection': {
          if (sectionIdx < sections.length - 1) {
            const nextSection = sections[sectionIdx + 1];
            if (nextSection.bars.length > 0) {
              setSelection({ sectionId: nextSection.id, barId: nextSection.bars[0].id });
            }
          } else {
            // Already last section — fall through to next bar
            if (barIdx < section.bars.length - 1) {
              setSelection({ sectionId, barId: section.bars[barIdx + 1].id });
            }
          }
          break;
        }
      }
    },
    [addBar, setSelection],
  );

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="chart-renderer w-full" style={{ position: 'relative' }}>
      <div ref={chartRef} style={{ position: 'relative' }}>
        {song.sections.map((section, sectionIndex) => (
          <SectionBlock
            key={section.id}
            section={section}
            sectionIndex={sectionIndex}
            selectedBarId={selection?.sectionId === section.id ? selection.barId : null}
            onSelectBar={(barId) => handleSelectBar(section.id, barId)}
            onNavigateBar={handleNavigateBar}
          />
        ))}

        <button
          onClick={() => addSection()}
          className="add-section-btn flex items-center gap-2 text-sm font-label tracking-widest uppercase text-ink-muted hover:text-ink-secondary transition-colors mt-4 group"
        >
          <span className="w-6 h-6 border border-dashed border-rule rounded flex items-center justify-center group-hover:border-ink-muted transition-colors text-base leading-none">
            +
          </span>
          Add Section
        </button>

        <AnnotationLayer
          chartRef={chartRef}
          annotationMode={annotationMode}
          onExitAnnotationMode={onExitAnnotationMode}
        />
      </div>
    </div>
  );
}